import { List, Record as record } from 'immutable';

import { construct, resolve, resolveAll } from '../helpers/LinkMapper';
import Fare from './Fare';
import { SplitTicketTypes } from '../constants/AppConstants';

const defaults = {
    splitId: '',
    fares: new List(),
    splitsSaving: 0,
    totalPrice: 0
};

class Split extends record(defaults) {
    static fromJSON (json = {}) {
        return construct(Split, json, {
            splitId: json.splitId || '',
            fares: resolveAll(json.fares).as(Fare),
            splitsSaving: resolve(json.splitsSaving).with(Number),
            totalPrice: resolve(json.totalPrice).with(Number),
        });
    }

    getOrigin() {
        return this.fares?.size > 0 ? this.fares.first().origin : undefined;
    }

    getDestination() {
        return this.fares?.size > 0 ? this.fares.last().destination : undefined;
    }

    isEmptyFares() {
        return !this.fares || this.fares.size === 0;
    }

    getMinSeatAvailability() {
        if (this.isEmptyFares()) {
            return false;
        }
        const seatAvailabilityArray = this.fares
            .map((fare) => fare.seatAvailability)
            .filter((seatAvailability) => typeof seatAvailability === 'number')
            .toArray();
        if (!seatAvailabilityArray.length) {
            return false;
        }
        return Math.min(...seatAvailabilityArray);
    }

    isStandard() {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isStandard());
    }

    isFirstClass() {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isFirstClass());
    }


    isReturn() {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isReturn());
    }

    isValidForReturn() {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every(fare => fare.isSplitReturn());
    }

    isGroupSave () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isGroupSave());
    }

    isDiscounted () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isDiscounted());
    }

    isAdvance () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.some((fare) => fare.isAdvance());
    }

    isFlexi () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isFlexi());
    }

    isAnytime () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isAnytime());
    }

    hasRailcard () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.hasRailcard());
    }

    isCarnet () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isCarnet());
    }

    isTravelcard () {
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every((fare) => fare.isTravelcard());
    }

    isSeatCompulsory () {
        return this.isAdvance();
    }

    getTicketType () {
        const splitTicketTypes = this.fares.map((splitFare) => splitFare?.ticketType);
        if (!splitTicketTypes.size) {
            return null;
        }
        if (this.isAdvance()) {
            return SplitTicketTypes.ADVANCE;
        }
        if (this.isFlexi() || this.isAnytime()) {
            return SplitTicketTypes.FLEXIBLE;
        }
        return SplitTicketTypes.LIMITED;
    }

    isValidForSingleReturn(anotherSplit) {
        if (!anotherSplit || !anotherSplit.fares || anotherSplit.fares.size === 0) {
            return false;
        }
        if (this.isEmptyFares()) {
            return false;
        }
        return this.fares.every(fare =>
            anotherSplit.fares.every(anotherSplitFare =>
                anotherSplitFare.isValidForSingleReturn && anotherSplitFare.isValidForSingleReturn(fare)
            )
        );
    }
}

export default Split;
